import { CloseButton, Table } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundTableRow } from '../../../components/DataTable';
import { LoadingRows } from '../../../components/LoadingRows';
import { TableRowCollapsible } from '../../../components/TableRowCollapsible';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { queryWithFilter } from '../../../util/api/queryWithFilter';
import { formatSecondsToHours } from '../../../util/formatting';
import type { FilterDto } from '../../../util/types/FilterDto';
import type { AttendanceDto, TotalAttendanceDto } from '../types/AttendanceDto';
import { IndividualAttendanceHeaders } from './IndividualAttendanceHeaders';
import { IndividualAttendanceRow } from './IndividualAttendanceRow';
import { KeyedRow } from '../../../components/KeyedRow';

interface AttendanceRowProps {
	totalAttendance: TotalAttendanceDto;
	filter: FilterDto;
	refreshTables: () => void;
}

export function GroupedAttendanceRow({
	totalAttendance,
	filter,
	refreshTables,
}: AttendanceRowProps) {
	const { organisation } = useParams();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const attendanceQuery = queryWithFilter<AttendanceDto[]>(
		`${organisation}/attendance/operator/${totalAttendance.operatorId}`,
		filter,
		isDropdownOpen,
	);

	const isDataFound = useMemo(() => {
		return (
			attendanceQuery.data?.value && attendanceQuery.data?.value.length > 0
		);
	}, [attendanceQuery.data?.value]);

	return (
		<>
			<KeyedRow onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
				<Table.Td>{totalAttendance.branchName}</Table.Td>
				<Table.Td>{totalAttendance.operatorName}</Table.Td>
				<Table.Td>{totalAttendance.logonCount}</Table.Td>
				<Table.Td>
					{formatSecondsToHours(totalAttendance.totalSeconds)}
				</Table.Td>
				<Table.Td>
					<CloseButton
						icon={
							!isDropdownOpen ? (
								<IconChevronDown {...defaultIconStyle} />
							) : (
								<IconChevronUp {...defaultIconStyle} />
							)
						}
						onClick={() => setIsDropdownOpen(!isDropdownOpen)}
					/>
				</Table.Td>
			</KeyedRow>
			<TableRowCollapsible isOpen={isDropdownOpen} columnRows={10}>
				<Table>
					<IndividualAttendanceHeaders />
					<Table.Tbody>
						{!attendanceQuery.isLoading ? (
							<>
								{!isDataFound && <NoDataFoundTableRow />}
								{attendanceQuery.data?.value.map((attendance) => (
									<IndividualAttendanceRow
										refreshTables={refreshTables}
										initialAttendance={attendance}
										key={attendance.id}
									/>
								))}
							</>
						) : (
							<LoadingRows rows={1} />
						)}
					</Table.Tbody>
				</Table>
			</TableRowCollapsible>
		</>
	);
}
